export const TIERS = ['anonymous', 'free', 'driver', 'trade'] as const;
export type Tier = (typeof TIERS)[number];

export interface TierLimits {
  checksPerDay: number;
  checksPerMonth: number;
  perMinute: number;
  garage: number;
  watchlist: number;
  compare: number;
  fullAi: boolean;
  chat: boolean;
  pdf: boolean;
  share: boolean;
  bulk: boolean;
  api: boolean;
  /** MOT reminder emails for cars in the garage (a paid feature, from Driver up). */
  reminders: boolean;
  /** Connecting an AI assistant over MCP (/mcp, /connect). Without it a connection gets only `about`, which says why. */
  mcp: boolean;
}

/** Defaults; the live values come from the plan_config collection so staff can tune them without a deploy. */
export const DEFAULT_TIER_LIMITS: Record<Tier, TierLimits> = {
  anonymous: {
    checksPerDay: 1, checksPerMonth: 60, perMinute: 3, garage: 0, watchlist: 0, compare: 0,
    fullAi: false, chat: false, pdf: false, share: false, bulk: false, api: false, reminders: false, mcp: false,
  },
  free: {
    checksPerDay: 3, checksPerMonth: 15, perMinute: 5, garage: 2, watchlist: 3, compare: 2,
    fullAi: true, chat: false, pdf: false, share: true, bulk: false, api: false, reminders: false, mcp: false,
  },
  driver: {
    checksPerDay: 20, checksPerMonth: 200, perMinute: 10, garage: 10, watchlist: 25, compare: 3,
    fullAi: true, chat: true, pdf: true, share: true, bulk: false, api: false, reminders: true, mcp: true,
  },
  trade: {
    checksPerDay: 250, checksPerMonth: 4000, perMinute: 60, garage: 500, watchlist: 500, compare: 3,
    fullAi: true, chat: true, pdf: true, share: true, bulk: true, api: true, reminders: true, mcp: true,
  },
};

export const TIER_LABELS: Record<Tier, string> = {
  anonymous: 'Guest',
  free: 'Free',
  driver: 'Driver',
  trade: 'Trade',
};

/**
 * Steady Auth plan id → tier. Steady Auth owns the RegFlags catalogue and names plans product-tier-cadence
 * (generatePlanId in the auth repo's packages/catalogue), e.g. regflags-trade-yearly. Unknown ids naming
 * trade/dealer/business map to trade, otherwise driver, so a plan staff add later still lands sensibly.
 */
export const DEFAULT_PLAN_TIERS: Record<string, Tier> = {
  'regflags-driver-monthly': 'driver',
  'regflags-driver-yearly': 'driver',
  'regflags-trade-monthly': 'trade',
  'regflags-trade-yearly': 'trade',
};

export function tierForPlan(planId: string | null | undefined, mapping: Record<string, Tier> = DEFAULT_PLAN_TIERS): Tier {
  if (!planId) return 'driver';
  return mapping[planId] ?? (TRADE_PLAN.test(planId) ? 'trade' : 'driver');
}

const TRADE_PLAN = /trade|dealer|business/i;
